import {
  getOAuthProviderUrl,
  setLastLoginMethod,
  type LoginMethod,
} from "@/components/auth/common/last-login-method";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { api } from "@/lib/api-client";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import type { UserOTPKindType } from "@repo/database";
import { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod/v4";
import type { DialogStep } from "./submit-inquiry";
const emailSchema = z.object({ email: z.string().email() });
type EmailFormValues = z.infer<typeof emailSchema>;
export function useInquiryAuth({
  savePendingAndPersist,
  setStep,
}: {
  savePendingAndPersist: () => void;
  setStep: (step: DialogStep) => void;
}) {
  const { setApiErrorsToForm } = useErrorFormatter();
  const [email, setEmail] = useState("");
  const [otpKind, setOtpKind] = useState<UserOTPKindType>("CREATE_ACCOUNT");
  const checkEmailMutation = api.auth.checkEmail.useMutation();
  const createAccountMutation =
    api.auth.createPasswordlessAccount.useMutation();
  const sendLoginOTPMutation = api.auth.sendLoginOTP.useMutation();

  const emailForm = useForm<EmailFormValues>({
    resolver: standardSchemaResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const onEmailSubmit: SubmitHandler<EmailFormValues> = async ({
    email: emailValue,
  }) => {
    try {
      const { isOAuth, oauthProvider, exists } =
        await checkEmailMutation.mutateAsync({
          email: emailValue,
          doNotSendToken: true,
        });

      if (isOAuth && oauthProvider) {
        const providerUrl = getOAuthProviderUrl(oauthProvider);
        if (providerUrl) {
          savePendingAndPersist();
          setLastLoginMethod(oauthProvider as LoginMethod);
          window.location.href = providerUrl;
          return;
        }
      }

      setEmail(emailValue);

      if (exists) {
        await sendLoginOTPMutation.mutateAsync({ email: emailValue });
        setOtpKind("LOGIN");
      } else {
        await createAccountMutation.mutateAsync({ email: emailValue });
        setOtpKind("CREATE_ACCOUNT");
      }

      setStep("auth_otp");
    } catch (err) {
      console.error(err);
      setApiErrorsToForm(err, emailForm);
    }
  };

  return {
    email,
    setEmail,
    otpKind,
    setOtpKind,
    emailForm,
    onEmailSubmit,
    checkEmailMutation,
    createAccountMutation,
    sendLoginOTPMutation,
  };
}
